<script setup>
import { computed } from 'vue';
import { format, parse } from 'date-fns';
import { useEnergyComparisonStore } from '../../stores/energyComparison';
import ProductUsp from './ProductUsp.vue';
import Greenscore from './Greenscore.vue';
import Tooltip from '../../../../../abstracts/elements/tooltip/tooltip.vue';
import { toCurrencyEurRounded, toCurrencyShortEur } from '../../../../../assets/js/helpers/currency';

const props = defineProps({
    product: {
        type: Object,
        required: true,
    },
});

const comparisonStore = useEnergyComparisonStore();

const getMontlyPaymentUsp = () => {
    if (props.product.rank < 6) {
        const firstFiveProducts = comparisonStore.products.slice(0, 5);
        const lowestMonthlyPriceProduct = firstFiveProducts.reduce((lowest, current) => {
            return current.month_price_without_discount < lowest.month_price_without_discount ? current : lowest;
        }, firstFiveProducts[0]);

        return lowestMonthlyPriceProduct.base_id === props.product.base_id;
    }
    return false;
};

const isCheapestSustainableProduct = () => {
    const firstSustainableProduct = comparisonStore.products.find(product => product.sustainability_score_total >= 8);

    return firstSustainableProduct && firstSustainableProduct.base_id === props.product.base_id;
};

const isCurrentVariableContract = computed(() => {
    return props.product.provider === comparisonStore.filters.currentProvider && props.product.tariff_type === 'variable' && props.product.disabled;
});

const defaultUsps = computed(() => {
    let uspList = [];

    if (isCurrentVariableContract.value) {
        uspList.push({
            icon: 'x-fa-exclamation-triangle',
            text: 'Variabel contract bij je huidige leverancier',
            tooltip: 'Als je niet overstapt wanneer je contract afloopt, ga je automatisch over op een variabel contract bij je huidige leverancier. Hier zie je wat je gaat betalen als je niet overstapt.',
        });
    } else if (props.product.fixed_end_date) {
        const endDate = parse(props.product.fixed_end_date, 'yyyy-MM-dd', new Date());

        uspList.push({
            icon: 'x-fa-check',
            text: `Vast contract t/m ${format(endDate, 'dd-MM-yyyy')}`,
        });

        if (props.product.fixed_end_date === '2026-12-31') {
            uspList.push({
                icon: 'x-fa-check',
                text: 'Contract eindigt wanneer salderen stopt',
            });
        }
    } else if (props.product.tariff_type === 'fixed' && props.product.contract_duration >= 12) {
        uspList.push({
            icon: 'x-fa-check',
            text: `${props.product.contract_duration_text} vaste prijs`,
        });
    } else if (props.product.tariff_type === 'variable') {
        uspList.push({
            icon: 'x-fa-check',
            text: 'Variabel contract',
        });
    } else if (props.product.tariff_type === 'dynamic') {
        uspList.push({
            icon: 'x-fa-check',
            text: 'Dynamisch contract',
        });
    } else if (props.product.tariff_type === 'dynamic_gas_fixed') {
        uspList.push({
            icon: 'x-fa-check',
            text: `Dynamische stroomtarieven, ${props.product.contract_duration_text} vaste gasprijs`,
        });
    }

    // TODO: Enable when usps are correct in BAAS
    // if (props.product.usps?.length > 0) {
    //     props.product.usps.forEach((usp) => {
    //         uspList.push({
    //             icon: 'x-fa-check',
    //             text: usp,
    //         });
    //     });
    // }

    return uspList;
});

const emit = defineEmits(['showProductInfo']);
const extraUsps = computed(() => {
    let uspList = [];

    // TODO: Better USPS for dynamic contracts
    if (comparisonStore.filters.contractTypes.includes('dynamic')) {
        return uspList;
    }

    if (comparisonStore.filters.electricityGenerated === true && comparisonStore.filters.electricityGeneration > 0) {
        const solarRevenue = (props.product.price_calculation.electricity_unit_settled_total + props.product.price_calculation.electricity_unit_settled_total_off_peak + props.product.price_calculation.electricity_surplus_price) * -1;
        if (props.product.provider === 'coolblue' && props.product.price_calculation.settling_costs_year == null) {
            uspList.push({
                icon: 'x-fa-solar-panel',
                text: `Incl. ${toCurrencyEurRounded(solarRevenue)} opbrengsten en hoger vastrecht`,
                tooltip: 'Bij Coolblue Energie betaal je geen terugleverkosten, maar betaal je met zonnepanelen meer vastrecht. Deze extra kosten hebben we meegerekend in de maandprijs.',
                infoTab: 'pricing',
            });
        } else {
            uspList.push({
                icon: 'x-fa-solar-panel',
                text: `Incl. ${toCurrencyEurRounded(solarRevenue)} opbrengsten en ${(toCurrencyEurRounded(props.product.price_calculation.settling_costs_year))} terugleverkosten`,
                infoTab: 'pricing',
            });
        }
        if (['mega'].includes(props.product.provider)) {
            uspList.push({
                icon: 'x-fa-trophy',
                text: 'Winnaar Beste Keuze Award',
                tooltip: `Keuze.nl heeft ${props.product.provider_name} uitgeroepen tot winnaar van de Beste Keuze Award 2025 in de categorie 'zonnepanelen'.`,
                infoTab: 'package',
            });
        }
    } else if (props.product.rank !== 1 && isCheapestSustainableProduct()) {
        uspList.push({
            icon: 'x-fa-check',
            text: 'Goedkoopste duurzame keuze',
            infoTab: 'green',
        });
    } else if (props.product.rank !== 1 && getMontlyPaymentUsp()) {
        uspList.push({
            icon: 'x-fa-check',
            text: 'Lager termijnbedrag',
            tooltip: 'Dit is niet het goedkoopste contract op jaarbasis, maar je maandelijkse vaste lasten zijn lager. Vergelijk op jaarprijs voor het goedkoopste contract inclusief korting.',
        });
    } else if (['budget-energie'].includes(props.product.provider)) {
        uspList.push({
            icon: 'x-fa-trophy',
            text: 'Winnaar Beste Keuze Award',
            tooltip: `Keuze.nl heeft ${props.product.provider_name} uitgeroepen tot winnaar van de Beste Keuze Award 2025 in de categorie 'laagste prijs'.`,
            infoTab: 'package',
        });
    } else if (['united-consumers'].includes(props.product.provider)) {
        uspList.push({
            icon: 'x-fa-trophy',
            text: 'Winnaar Beste Keuze Award',
            tooltip: `Keuze.nl heeft ${props.product.provider_name} uitgeroepen tot winnaar van de Beste Keuze Award 2025 in de categorie 'klanttevredenheid'.`,
            infoTab: 'package',
        });
    }

    return uspList;
});



const handleClick = (tab) => {
    if (tab) {
        emit('showProductInfo', tab);
    }
}

const hasVVVIncentive = computed(() => {
    return comparisonStore.hasVVVIncentive && props.product.available_orderline;
});


</script>

<template>
    <div class="energy-product-usp-list">
        <!-- Incentive Label (Ads only VVV) -->
        <ProductUsp v-if="hasVVVIncentive" class="incentive-usp clickable" @click="handleClick('package')">
            <img src="../../../../../web/assets/campaign/vvv-cadeaukaart.webp" height="18" alt="VVV cadeaubon"
                title="VVV cadeaubon" />
            <span>Gratis VVV cadeaubon t.w.v. €10,-</span>
        </ProductUsp>

        <!-- Default USP('s)) -->
        <ProductUsp v-for="usp in defaultUsps" :key="usp.text" :icon="usp.icon" :text="usp.text"
            :class="usp.infoTab ? 'clickable' : ''" @click="handleClick(usp.infoTab)">
            <template v-if="usp.tooltip" v-slot:tooltip>
                <Tooltip position="top-right">
                    <template #text>
                        {{ usp.tooltip }}
                    </template>
                </Tooltip>
            </template>
        </ProductUsp>

        <!-- Greenscore USP -->
        <ProductUsp v-if="!isCurrentVariableContract" class="clickable" icon="x-fa-check"
            :text="`Groenscore ${product.sustainability_score_total}`" @click="handleClick('green')">
            <template v-slot="tooltip">
                <Greenscore :score="product.sustainability_score_total" />
            </template>
        </ProductUsp>

        <!-- Product & filter based USP's -->
        <ProductUsp v-for="usp in extraUsps" :key="usp.text" :icon="usp.icon" :text="usp.text"
            :class="usp.infoTab ? 'clickable' : ''" @click="handleClick(usp.infoTab)">
            <template v-if="usp.tooltip" v-slot:tooltip>
                <Tooltip position="top-right">
                    <template #text>
                        {{ usp.tooltip }}
                    </template>
                </Tooltip>
            </template>
        </ProductUsp>


    </div>
</template>

<style lang="scss">
@use 'assets/scss/all' as *;

.energy-product-usp-list {
    @include flex-col-start;
    gap: 8px;
}
</style>
